import { JSONTransformer } from '@atlaskit/editor-json-transformer/JSONTransformer-2';
import type { JSONNode } from '@atlaskit/editor-json-transformer/types';
import type { Fragment, Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { MentionMap } from '@atlassian/ai-model-io/utils/mention-map';
import { ProseMirrorContentStatistics } from '@atlassian/ai-model-io/utils/prosemirror-content-statistics';
import type {
	ContentStatistics,
	ContextSelectionScope,
} from '@atlassian/ai-model-io/utils/prosemirror-content-statistics';

import {
	getOutsideSyncBlockRuns,
	isImproveDescriptionSyncBlockFixEnabled,
} from '../../../pm-plugins/improve-description-sync-block';
import type {
	EditorPluginAIConfigItem,
	UpdateIdMap,
} from '../../prebuilt/config-items/config-items';
import { sliceOrNodeToMarkdown } from '../../prebuilt/config-items/utils/slice-or-node-to-markdown';
import { convoAIApiUrl } from '../../prebuilt/utils';
import { getApplyContext, type ApplyContext } from '../selection';
import { getSupportedMarkdowns } from './get-supported-markdown-from-schema';
import type { AIExperienceMachineContextForRequest, RequestOptions, ResponseObject } from './types';

type Options = {
	callback: (event: ResponseObject) => void;
	configItem: EditorPluginAIConfigItem;
	context: AIExperienceMachineContextForRequest;
	mentionMap?: MentionMap;
	updateIdMap: UpdateIdMap;
};

/**
 * Reads the inline ADF content back out of a doc-wrapped edge fragment.
 *
 * `getApplyContext` wraps the fragment in its target node's type so the schema
 * accepts it; only the inline content is sent, since a consumer already holds the
 * target node it will rebuild.
 */
const encodeInlineContent = (
	serializer: JSONTransformer,
	node: PMNode | undefined,
): Array<JSONNode | undefined> | undefined => {
	if (!node) {
		return undefined;
	}

	// An edge with no content either side of the selection encodes to an empty
	// array, which is meaningful - it is not the same as an absent edge.
	return serializer.encode(node, { keepNestedTables: true }).content?.[0]?.content ?? [];
};

/** Maps apply context to the request body's snake_case convention. */
const buildApplyContextPayload = (
	applyContext: ApplyContext,
	editorSchema: Schema,
	hasRealTimeCollab: boolean,
) => {
	// The edges describe content the user did not select, and unlike the prompt
	// input they may be written back to the document. They must therefore be a
	// faithful copy, so they are encoded without the mentionMap: it synthesises
	// attrs.text on a mention that has none, which would hand a consumer a mention
	// whose text did not come from the document. See toJSON's isMentionNode branch.
	const edgeSerializer = new JSONTransformer(editorSchema);

	return {
		target_local_ids: applyContext.targetLocalIds,
		has_real_time_collab: hasRealTimeCollab,
		selected_text: applyContext.selectedText,
		prefix_content: encodeInlineContent(edgeSerializer, applyContext.prefixDoc),
		suffix_content: encodeInlineContent(edgeSerializer, applyContext.suffixDoc),
	};
};

const buildContentAri = ({
	product,
	objectId,
	cloudId,
}: {
	cloudId?: string;
	objectId?: string | number;
	product: string;
}): string => {
	return `ari:cloud:${product.toLowerCase()}:${cloudId}:content/${objectId}`;
};

/**
 * A function that will make the payload for the Convo AI API.
 *
 * @see https://convo-ai.us-east-1.staging.atl-paas.net/api/swagger-ui/index.html#/ai-features-controller/editorStream
 */
export const getRequestOptions4ConvoAI = ({
	context,
	configItem,
	mentionMap,
	updateIdMap,
	callback,
}: Options): RequestOptions => {
	const { aiProvider, userInput, editorView, positions, intl, latestPromptResponse } = context;
	const editorSchema = editorView.state.schema;
	const { product, objectId, getChannelVariables } = aiProvider;
	const cloudId = getChannelVariables?.('rovo-agent')?.cloudId;

	let contentAri: string | undefined;
	// Currently CC and Jira pass in the objectID. According to the BE implementation this content ari is required.
	// for CC, Jira and Trello. This will make this property optional from FE perspective and if the BE reqruies this
	// then they can return an error when needed. This will unblock all product from adopting this.
	if (objectId !== undefined && cloudId !== undefined) {
		contentAri = buildContentAri({ product, objectId, cloudId });
	}

	const featureId = configItem.featureId;
	if (!featureId) {
		return {
			error: `Unknown feature id: ${configItem.intentSchemaId}`,
			endpoint: configItem.endpoint || convoAIApiUrl,
		};
	}

	const { experienceId, endpoint } = configItem;
	const aiSessionId = context.analyticsContext?.aiSessionId;
	const idMap = {};

	// if there is no selection, we want to pass only the doc
	// if there is selection, we want to pass only the selection (if we pass doc and selection, the doc will be ignored by BE)
	let needDoc = context.isEmpty;
	let needSelection = !context.isEmpty;

	if (configItem.alwaysNoDocAndSelection) {
		// for Brainstorm, Jira Draft reply, don't send document (we have history feedback that LLM was using the document too much)
		needDoc = false;
		needSelection = false;
	} else if (configItem.alwaysNeedDoc) {
		// for Jira Improve description
		needDoc = true;
		needSelection = false;
	} else if (configItem.canTogglePageKnowledge) {
		// for Free generate and interrogation
		// For range selection and usePageKnowledge, we're sending both doc and selection
		needDoc = context.usePageKnowledge;
	}

	// Check for experiment first to fire exposure event, otherwise there will be imbalance.
	const isADFPrompt = configItem.adfPrompt;

	let docContent: string | undefined;
	let selectionContent: string | undefined;
	let contentStatistics: ContentStatistics | undefined;
	let applyContextPayload: ReturnType<typeof buildApplyContextPayload> | undefined;
	if (isADFPrompt) {
		const serializer = new JSONTransformer(editorSchema, mentionMap);

		// Only the ADF prompts are candidates for a backend-applied edit. Applying an
		// edit outside the editor means splicing the generated content into the
		// stored document as ADF, and the markdown prompts return markdown-plus, so
		// there would be nothing to splice.
		const applyContext = isExperimentEnabled('platform_editor_quick_actions_be_update')
			? getApplyContext(editorView.state, positions)
			: undefined;
		applyContextPayload =
			applyContext &&
			buildApplyContextPayload(
				applyContext,
				editorSchema,
				context.aiProvider.getHasRealTimeCollab?.() ?? false,
			);
		docContent = needDoc
			? JSON.stringify(serializer.encode(editorView.state.doc, { keepNestedTables: true }))
			: undefined;

		let contextSelectionScope: ContextSelectionScope = 'fullDocument';
		let selectionDoc: PMNode | null = null;
		if (needSelection) {
			const docNodeType = editorSchema.nodes.doc;
			const doc = editorView.state.doc;
			const selectionSlice = doc.slice(positions[0], positions[1]);
			const sliceContent = selectionSlice.content;

			callback({
				type: 'update selectionMetaData',
				context: {
					selectionMetaData: {
						sliceOpenStart: selectionSlice.openStart,
						sliceOpenEnd: selectionSlice.openEnd,
					},
				},
			});

			if (docNodeType.validContent(sliceContent)) {
				selectionDoc = docNodeType.createAndFill(null, selectionSlice.content);
				selectionContent = selectionDoc
					? JSON.stringify(serializer.encode(selectionDoc, { keepNestedTables: true }))
					: undefined;
			} else {
				const startPos = doc.resolve(positions[0]);
				const endPos = doc.resolve(positions[1]);
				const adfResponseNodeWrappers: string[] = [];
				/**
				 * When prosemirror creates slice, it will have node
				 * 	starting from (shared depth + 1) level of start and end position.
				 * So finding parent node that can set as doc content should be
				 * 	start at shared depth level.
				 */
				const sameParentDepth = startPos.sharedDepth(endPos.pos);
				let currentDepth = sameParentDepth;
				let docContentNode: Fragment | PMNode | null = selectionSlice.content;
				while (currentDepth > 0) {
					const parentNode = startPos.node(currentDepth);
					const parentNodeType = parentNode.type;
					docContentNode = parentNodeType.createAndFill(null, docContentNode);
					if (docContentNode) {
						adfResponseNodeWrappers.push(parentNodeType.name);
						selectionDoc = docNodeType.createAndFill(null, docContentNode);
						if (selectionDoc) {
							selectionContent = JSON.stringify(
								serializer.encode(selectionDoc, { keepNestedTables: true }),
							);
							break;
						} else {
							currentDepth--;
						}
					} else {
						break;
					}
				}
				contextSelectionScope = 'partialSelection';
				callback({
					type: 'update adfResponseNodeWrappers',
					context: {
						adfResponseNodeWrappers,
					},
				});
			}
		}
		const nodeForStats = needDoc ? editorView.state.doc : selectionDoc;
		if (nodeForStats) {
			const pmStats = new ProseMirrorContentStatistics(nodeForStats);
			contentStatistics = pmStats.collectStatistics();
			contentStatistics = { ...contentStatistics, contextSelectionScope };
		}
	} else {
		const outsideRuns = isImproveDescriptionSyncBlockFixEnabled(configItem.key)
			? getOutsideSyncBlockRuns(editorView.state.doc)
			: undefined;
		if (outsideRuns?.length === 0) {
			// The description is only synced blocks, which Improve description
			// must not rewrite, so there is nothing to send.
			return {
				response: {
					type: 'response too similar',
					cacheKey: 'improve-description-source-sync-block-only',
					modelInput: {},
				},
			};
		}
		const docSource = outsideRuns
			? { slice: editorView.state.doc.slice(outsideRuns[0].from, outsideRuns[0].to) }
			: { node: editorView.state.doc };
		const doc = needDoc
			? sliceOrNodeToMarkdown({
					...docSource,
					editorView,
					convertTo: 'markdown-plus',
					existingIdMap: idMap,
					updateIdMap,
					mentionMap,
					aiSessionId,
				})
			: undefined;
		docContent = doc?.markdown;

		const selection = needSelection
			? sliceOrNodeToMarkdown({
					slice: editorView.state.doc.slice(positions[0], positions[1]),
					editorView,
					convertTo: 'markdown-plus',
					existingIdMap: doc?.idMap,
					updateIdMap,
					mentionMap,
					aiSessionId,
				})
			: undefined;

		docContent = doc?.markdown;
		selectionContent = selection?.markdown;
		contentStatistics = doc?.contentStatistics || selection?.contentStatistics;
	}

	const additionalProps = aiProvider.getAdditionalProps && aiProvider.getAdditionalProps?.();

	/**
	 * Whenever configItem has a getRequestPayload, it should be used to build the payload.
	 * Currently it is defined for Jira Improve description, Generate Escalation Notes,
	 * 	Draft reply and Incident Smart Comments.
	 */
	const payload = configItem.getRequestPayload?.({
		aiSessionId,
		contentAri,
		locale: intl.locale,
		isEmpty: context.isEmpty,
		doc: docContent,
		selection: selectionContent,
	});

	return {
		doc: docContent,
		selection: selectionContent,
		contentStatistics: contentStatistics,
		isConvoAI: true,
		experienceId: experienceId || 'editor',
		endpoint: endpoint || convoAIApiUrl,
		payload: JSON.stringify(
			payload || {
				ai_feature_input: {
					feature_args: {
						// NOTE: we want to ensure we use undefined instead of null. This is because the JSON Serializer will remove props
						// that are undefined, but will not remove props that are null.
						user_input: userInput || undefined,
						// Previously Assistance service sent a `document_title` prop which was used only by jira for situations where nothing was selected
						// during free generate. This will ensure that the BE has some context about the issue
						document_title: additionalProps?.issue_summary,
						selected_context: latestPromptResponse || selectionContent || undefined,
						document_context: docContent || undefined,
						supported_markdown_nodes: getSupportedMarkdowns(editorSchema),
						output_mime_type: isADFPrompt ? 'text/adf' : 'text/markdown',
						// for translate
						transformation_subtype: configItem.lang,
					},
					feature: featureId,
					/**
					 * We can still get a response from the server if the contentAri is not valid
					 * but we will get 500, internal server error if the contentAri is undefined.
					 * It is not required for a prompt like improve writing so we can ignore it.
					 * But we should still log an analytics event to track when this occurs.
					 */
					content_ari: contentAri,
					ai_session_id: aiSessionId,
					locale: intl.locale,
					apply_context: applyContextPayload,
				},
			},
		),
	};
};
